import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { PlusCircle, Edit, Trash2, GripVertical, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { useSite } from '../../contexts/SiteContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminPages() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { refreshPages } = useSite();

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const response = await axios.get(`${API}/pages`, { withCredentials: true });
      setPages(response.data);
    } catch (error) {
      console.error('Error fetching pages:', error);
      toast.error('Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (slug) => {
    try {
      await axios.delete(`${API}/pages/${slug}`, { withCredentials: true });
      setPages(pages.filter(p => p.slug !== slug));
      refreshPages();
      toast.success('Page deleted');
    } catch (error) {
      toast.error('Failed to delete page');
    }
  };

  const toggleMenuVisibility = async (page) => {
    try {
      await axios.put(`${API}/pages/${page.slug}`, 
        { show_in_menu: !page.show_in_menu },
        { withCredentials: true }
      );
      setPages(pages.map(p => 
        p.slug === page.slug ? { ...p, show_in_menu: !p.show_in_menu } : p
      ));
      refreshPages();
      toast.success(page.show_in_menu ? 'Removed from menu' : 'Added to menu');
    } catch (error) {
      toast.error('Failed to update page');
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4" />
          <div className="h-64 bg-muted rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="admin-pages">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Pages</h1>
          <p className="text-muted-foreground">Manage site pages (shown in navigation)</p>
        </div>
        <Link to="/admin/pages/new">
          <Button data-testid="new-page-btn">
            <PlusCircle className="mr-2 h-4 w-4" />
            New Page
          </Button>
        </Link>
      </div>

      {pages.length > 0 ? (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium w-10">Order</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Title</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Slug</th>
                <th className="px-4 py-3 text-left text-sm font-medium">In Menu</th>
                <th className="px-4 py-3 text-right text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pages.map((page) => (
                <tr key={page.id} className="border-t border-border" data-testid={`page-row-${page.slug}`}>
                  <td className="px-4 py-3 text-muted-foreground">
                    <GripVertical className="h-4 w-4" />
                  </td>
                  <td className="px-4 py-3">
                    <Link 
                      to={`/admin/pages/${page.slug}`}
                      className="font-medium hover:text-primary transition-colors"
                    >
                      {page.title}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    /page/{page.slug}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleMenuVisibility(page)}
                      className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full cursor-pointer transition-colors ${
                        page.show_in_menu 
                          ? 'bg-green-500/10 text-green-500 hover:bg-green-500/20' 
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                      data-testid={`toggle-menu-${page.slug}`}
                    >
                      {page.show_in_menu ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                      {page.show_in_menu ? 'Visible' : 'Hidden'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Link to={`/admin/pages/${page.slug}`}>
                        <Button variant="ghost" size="sm" data-testid={`edit-page-${page.slug}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                      </Link>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" data-testid={`delete-page-${page.slug}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Page</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{page.title}"? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleDelete(page.slug)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-16 border border-dashed border-border rounded-lg">
          <p className="text-muted-foreground mb-4">No pages yet</p>
          <Link to="/admin/pages/new">
            <Button>Create your first page</Button>
          </Link>
        </div>
      )}
    </div>
  );
}
